import React, { useState } from 'react';
import {
  Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Typography, Tooltip, Link,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { OpenInNew } from '@mui/icons-material';
import Panel from '../crt/Panel';
import { seoUrlIndexAPI } from '../../services/api';

const STATE_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  'Submitted and indexed':                       'success',
  'Indexed, not submitted in sitemap':           'success',
  'Crawled - currently not indexed':             'warning',
  'Discovered - currently not indexed':          'warning',
  'Page with redirect':                          'default',
  'Alternate page with proper canonical tag':    'default',
  'Duplicate without user-selected canonical':   'warning',
  'Duplicate, Google chose different canonical': 'warning',
  'Excluded by ‘noindex’ tag':                   'error',
  'Not found (404)':                             'error',
  'Soft 404':                                    'error',
  'Server error (5xx)':                          'error',
  'Blocked by robots.txt':                       'error',
};

const stateColor = (s?: string) =>
  STATE_COLOR[s || ''] || 'default';

const UrlIndexPanel: React.FC = () => {
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [filter, setFilter] = useState<string>('');

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['seo-url-index-summary'],
    queryFn: seoUrlIndexAPI.summary,
    staleTime: 5 * 60_000,
  });

  // Default to first site when summary loads
  React.useEffect(() => {
    if (summary?.sites?.length && !selectedSite) {
      setSelectedSite(summary.sites[0].site);
    }
  }, [summary, selectedSite]);

  const { data: detail } = useQuery({
    queryKey: ['seo-url-index-detail', selectedSite, filter],
    queryFn: () => seoUrlIndexAPI.detail(selectedSite, filter || undefined, 500),
    enabled: !!selectedSite,
    staleTime: 5 * 60_000,
  });

  const sites: Array<{ site: string; total: number; by_state: Record<string, number> }> =
    summary?.sites || [];

  if (summaryLoading) {
    return (
      <Panel label="GSC URL INDEX STATE">
        <Typography variant="caption">loading…</Typography>
      </Panel>
    );
  }

  if (sites.length === 0) {
    return (
      <Panel label="GSC URL INDEX STATE">
        <Typography variant="body2" color="text.secondary">
          No URL Inspection data yet. The cron runs daily at 06:55 UTC; first
          sweep will populate ~316 URLs across SL + MB. Trigger manually:{' '}
          <code>python -m etl.cli.gsc_url_inspection_run --apply</code>
        </Typography>
      </Panel>
    );
  }

  return (
    <Panel label="GSC URL INDEX STATE">
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Per-URL state from GSC URL Inspection API. Last sweep:{' '}
          {summary?.last_inspected_at
            ? new Date(summary.last_inspected_at).toLocaleString()
            : '—'}
        </Typography>

        {/* Per-site rollup tiles */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          {sites.map((s) => (
            <Box
              key={s.site}
              onClick={() => { setSelectedSite(s.site); setFilter(''); }}
              sx={{
                cursor: 'pointer',
                p: 2,
                minWidth: 280,
                border: '1px solid',
                borderColor: selectedSite === s.site ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {s.site === 'spoutlore' ? 'SPOUT LORE' : 'MALL BRATS'}
              </Typography>
              <Typography variant="h5">{s.total} URLs</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {Object.entries(s.by_state)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([state, count]) => (
                    <Chip
                      key={state}
                      label={`${count} ${state.replace('currently ', '').slice(0, 30)}`}
                      size="small"
                      color={stateColor(state)}
                      variant={filter === state && selectedSite === s.site ? 'filled' : 'outlined'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSite(s.site);
                        setFilter(filter === state && selectedSite === s.site ? '' : state);
                      }}
                    />
                  ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* Drill-down table */}
        {selectedSite && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Site</InputLabel>
                <Select
                  label="Site"
                  value={selectedSite}
                  onChange={(e) => { setSelectedSite(e.target.value); setFilter(''); }}
                >
                  {sites.map((s) => (
                    <MenuItem key={s.site} value={s.site}>{s.site}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>Filter by state</InputLabel>
                <Select
                  label="Filter by state"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <MenuItem value="">— all —</MenuItem>
                  {Object.keys(sites.find((x) => x.site === selectedSite)?.by_state || {}).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {detail?.rows?.length || 0} rows
              </Typography>
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>URL</TableCell>
                    <TableCell>STATE</TableCell>
                    <TableCell>VERDICT</TableCell>
                    <TableCell>LAST CRAWLED</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail?.rows || []).slice(0, 200).map((r: any) => {
                    const path = r.url.replace(/^https?:\/\/[^/]+/, '');
                    return (
                      <TableRow key={r.url} hover>
                        <TableCell sx={{ maxWidth: 480 }}>
                          <Tooltip title={r.url}>
                            <Link
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{
                                fontFamily: 'monospace', fontSize: '12px',
                                color: 'inherit',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                              }}
                            >
                              {path} <OpenInNew fontSize="inherit" />
                            </Link>
                          </Tooltip>
                          {r.google_canonical && r.google_canonical !== r.url && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                              ↪ Google canonical: {r.google_canonical.replace(/^https?:\/\/[^/]+/, '')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={r.coverage_state || '—'}
                            size="small"
                            color={stateColor(r.coverage_state)}
                          />
                        </TableCell>
                        <TableCell>{r.verdict || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">
                            {r.last_crawl_time
                              ? new Date(r.last_crawl_time).toLocaleDateString()
                              : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </Panel>
  );
};

export default UrlIndexPanel;
